<template>
  <!-- eslint-disable max-len -->
  <section>
    <div class="verify-info-edit-desktop">
      <form
        ref="form"
      >
        <div class="c-hairline mt-8" />
        <div class="py-8 flex flex-row flex-no-wrap">
          <div class="w-1/2 max:pr-xl">
            <h3
              class="font-bold"
            >
              Move Information
            </h3>
            <!-- Default non-business inputs -->
            <InputText
              v-model.trim="email"
              label="Email Address"
              placeholder="Email Address"
              name="email"
              class="mt-2"
              :has-error="errors.email.hasError"
              :disabled="!primaryBusiness"
              @blur="analytics.input.email();"
              @input="errors.email.hasError = false"
            >
              <span
                v-if="errors.email.hasError"
                slot="helper"
              >
                {{ errors.email.message }}
              </span>
            </InputText>
            <div class="mt-5">
              <label class="reminder_email--label">
                Check this box to opt-in to receive a one-time reminder email.
                <input
                  id="optin-mail-reminder"
                  :checked="optInMailReminder"
                  class="mt-3"
                  type="checkbox"
                  @click="optInMailReminder = !optInMailReminder; analytics.click.optInReminder(optInMailReminder)"
                >
                <span class="checkmark" />
              </label>
            </div>
            <div class="mt-5">
              <p>
                By opting in, if you choose not to complete your Change-of-Address
                (COA), USPS will send you a one-time email reminder to come back
                and finalize your request. Your COA will not be activated until
                it is complete.
              </p>
            </div>
            <div class="flex flex-row mt-4">
              <InputPhone
                v-model="phoneNumber"
                label="Phone Number"
                placeholder="(000) 000-0000"
                name="phone"
                class="flex-1"
                :has-error="errors.phoneNumber.hasError"
                :disabled="!primaryBusiness"
                @blur="analytics.input.phone();"
                @input="errors.phoneNumber.hasError = false"
              >
                <span
                  v-if="errors.phoneNumber.hasError"
                  slot="helper"
                >
                  {{ errors.phoneNumber.message }}
                </span>
              </InputPhone>
              <div class="block pl-4" />
              <InputDropdown
                v-model="phoneType"
                label="Phone Type"
                name="phone-type"
                class="flex-1"
                :disabled="!primaryBusiness"
                :has-error="errors.phoneType.hasError"
                @blur="analytics.input.phoneType();"
              >
                <option value="HOME">
                  Home
                </option>
                <option value="MOBILE">
                  Mobile
                </option>
                <span
                  v-if="errors.phoneType.hasError"
                  slot="helper"
                >
                  {{ errors.phoneType.message }}
                </span>
              </InputDropdown>
            </div>
            <InputDropdown
              v-model="forwardType"
              label="Type of Move"
              class="mt-2"
              name="forward-type"
              :has-error="errors.forwardType.hasError"
              @input="$event && analytics.click.forwardType($event);"
            >
              <option
                value=""
                aria-describedby="mover-type-field"
              >
                Select
              </option>
              <option value="PERMANENT">
                Permanent
              </option>
              <option value="TEMPORARY">
                Temporary
              </option>
              <span
                v-if="errors.forwardType.hasError"
                slot="helper"
              >
                {{ errors.forwardType.message }}
              </span>
            </InputDropdown>
            <InputDatePicker
              v-model="startDate"
              :label="forwardType !== 'TEMPORARY' ? 'Mail Forwarding Date' : 'Start Forwarding'"
              :min="minStartDate"
              :max="maxStartDate"
              desktop
              name="forward-start-date"
              class="mt-2"
              :has-error="errors.startDate.hasError"
              @input="analytics.input.startDate();"
            >
              <span slot="helper">
                {{ errors.startDate.message }}
              </span>
            </InputDatePicker>
            <InputDatePicker
              v-if="forwardType === 'TEMPORARY'"
              v-model="stopDate"
              :min="minStopDate"
              :max="maxStopDate"
              :open-date="minStopDate"
              desktop
              label="End Forwarding"
              name="forward-stop-date"
              class="mt-3"
              :has-error="errors.stopDate.hasError"
              @input="analytics.input.stopDate();"
            >
              <span slot="helper">
                {{ errors.stopDate.message }}
              </span>
            </InputDatePicker>
          </div>
          <div class="w-1/2 pl-xl">
            <h3>More Information</h3>
            <template>
              <p class="mt-4">
                <span class="font-bold">Email</span><br>
                Only the email address of the "Primary" business listed in the saved businesses section can be edited. Changes to the email address will be applied to all other business names and addresses.
              </p>
            </template>
            <template>
              <p class="mt-4">
                <span class="font-bold">Phone</span><br>
                Only the phone and phone type of the "Primary" business listed in the saved businesses section can be edited. Changes to the phone or phone type will be applied to all other business names and addresses.
              </p>
            </template>
            <!-- Permanent forwardType messaging -->
            <template v-if="forwardType === 'PERMANENT'">
              <p class="mt-4">
                <span class="font-bold">Permanent</span><br>
                Choose this option if you don't plan to move back to your old address (most moves are permanent).
              </p>
            </template>
            <!-- Temporary and default forwardType messaging-->
            <template v-if="!forwardType || forwardType === '' || forwardType === 'TEMPORARY'">
              <p class="mt-4">
                <span class="font-bold">Temporary</span><br>
                Choose this option if you plan to move back to your old address in less than six months.
              </p>
              <p class="mt-4">
                If you plan on moving to a new address for less than 6 months, then plan on moving to a different residence that's not the same as your old address, choose "Permanent," and fill out another Change-of-Address request at that time.
              </p>
            </template>
            <p class="mt-4">
              <span class="font-bold">Mail Forwarding Date</span><br>
              Enter the date you'd like us to begin forwarding your mail to your new address. The date you enter must not be more than 30 days prior to today, or more than 3 months from today. Mail should arrive at your new address 7-10 postal business days after your move effective date OR your file date, whichever date is later.
            </p>
            <template v-if="forwardType !== 'PERMANENT'">
              <p class="mt-4">
                The minimum length for filing a temporary Change-of-Address is 15 days.
              </p>
              <p class="mt-4">
                The initial forwarding period is restricted to 6 months, but can be extended up to 1 year.
              </p>
            </template>
          </div>
        </div>
        <div class="c-hairline" />
        <MultipleBusinessAddresses
          ref="multipleAddressesComponent"
          :forward-type-val="forwardType"
          :is-simplify="false"
          :start-date-val="startDate"
          :stop-date-val="stopDate"
          :business-whole-company-val="businessWholeCompany"
          :email-val="email"
          :phone-number-val="phoneNumber"
          :phone-type-val="phoneType"
          :opt-in-mail-reminder-val="optInMailReminder"
          view-mode="verifyEdit"
          this.errors
          @primary="setPrimaryBusiness"
          @validateForm="validateForm()"
          @fieldErrors="(fieldErrors)=>updateFieldErrors(fieldErrors)"
          @fieldErrorsMilitary="updateFieldErrorsMilitary"
          @disableNonBusinessMovers="disableMoverType"
        />
      </form>
      <Modal
        id="new-constr-close-x"
        v-model="newConstructionModal"
      >
        <ModalNewConstruction
          :street-address="newStreetAddress"
          :city="newCity"
          :state="newState"
          :zip-code="newZipCode"
          :urbanization-name="newUrbanizationName"
          @reset="newConstructionModal = false"
          @override="newConstructionOverride"
        />
      </Modal>
      <Modal
        ref="emailReminderOptOutModal"
        v-model="emailReminderOptOutModal"
      >
        <h4>One-Time Email Opt-Out</h4>
        <p class="mt-3">
          You've opted-out of the one-time reminder email if you don't complete this Change-of-Address transaction.
        </p>
        <p class="mt-3">
          You will still receive confirmation emails if you complete your transaction.
        </p>
        <button
          class="c-btn mt-3"
          @click="emailReminderOptOutModal=false"
        >
          Close
        </button>
      </Modal>
      <Modal
        ref="coaCheckModal"
        v-model="coaCheckModal"
      >
        <h4 class="font-bold">
          Based on your response...
        </h4>
        <p class="mt-3">
          A Change-of-Address already exists matching the name and address you've entered. To make changes to your current Change-of-Address, visit
          <a
            class="c-link c-link--inline"
            :href="this.$sanitize($options.coacUrl)"
            rel="noopener noreferrer"
          >
            {{ $options.coacUrl }}
          </a>, enter your new ZIP Code™ and 16-digit confirmation code, which can be located within your confirmation email and the Confirmation Notification Letter you received at your new address.
        </p>
      </Modal>
    </div>
  </section>
</template>

<script>
/* eslint-disable default-case */
import {
  InputDatePicker,
  InputDropdown,
  InputPhone,
  InputText,
  Modal,
} from '@mgo/ui/lib/components';
import { desktopAnalytics } from '../VerifyInfoEdit/analytics';
import ModalNewConstruction from '../../components/ModalNewConstruction.vue';

// the mixins and shared logic
import addressViolations from '../../mixins/addressViolations';
import datePicker from '../../mixins/datePicker';
import emailReminder from '../../mixins/emailReminder';
import moverType from '../../mixins/moverType';
import newConstruction from '../../mixins/newConstruction';
import rapidKey from '../../mixins/rapidKey';
import destinationManager from '../../mixins/destinationManager';
import verifyInfoEditMixin from './mixin';
import MultipleBusinessAddresses from '../../components/MultipleAddresses/desktop.vue';

export default {
  name: 'DesktopVerifyInfoEditMB',
  components: {
    InputDatePicker,
    InputDropdown,
    InputPhone,
    InputText,
    Modal,
    ModalNewConstruction,
    MultipleBusinessAddresses,
  },
  mixins: [
    addressViolations,
    datePicker,
    emailReminder,
    moverType,
    newConstruction,
    rapidKey,
    verifyInfoEditMixin,
    destinationManager,
  ],
  computed: {
    analytics() {
      return desktopAnalytics();
    },
  },
};
</script>
